import { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, Gift, Pencil, Plus, RefreshCw, Save, Shuffle, Trash2, Trophy } from "lucide-react";
import { api } from "../lib/api";

const blankGiveaway = { title: "", description: "", entry_cost_amount: 1, prizes: [""] };
const blankEntry = { username: "", display_name: "", source: "manual" };
const inputClass = "w-full h-10 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))] disabled:opacity-50";

function normalizePrizeList(value) {
  const source = Array.isArray(value) ? value : String(value || "").split(/[\r\n,]+/);
  const prizes = source
    .map((item) => item.trim())
    .filter(Boolean);
  return prizes.length ? prizes : [];
}

function giveawayPayload(form) {
  return {
    title: form.title,
    description: form.description,
    entry_cost_amount: Math.max(0, Number(form.entry_cost_amount) || 0),
    prizes: normalizePrizeList(form.prizes),
  };
}

export default function Giveaways() {
  const [giveaways, setGiveaways] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [giveawayForm, setGiveawayForm] = useState(blankGiveaway);
  const [editForm, setEditForm] = useState(blankGiveaway);
  const [editing, setEditing] = useState(false);
  const [entryForm, setEntryForm] = useState(blankEntry);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [overlayDuration, setOverlayDuration] = useState(60);

  const selected = useMemo(
    () => giveaways.find((giveaway) => giveaway.id === selectedId) || giveaways[0] || null,
    [giveaways, selectedId]
  );
  const activeGiveaways = useMemo(
    () => giveaways.filter((giveaway) => giveaway.status !== "completed"),
    [giveaways]
  );
  const completedGiveaways = useMemo(
    () => giveaways.filter((giveaway) => giveaway.status === "completed"),
    [giveaways]
  );

  useEffect(() => {
    if (!selected) {
      setEditForm(blankGiveaway);
      setEditing(false);
      return;
    }
    setEditForm({
      title: selected.title || "",
      description: selected.description || "",
      entry_cost_amount: selected.entry_cost_amount ?? 1,
      prizes: selected.prizes?.length ? [...selected.prizes] : [""],
    });
    setEditing(false);
  }, [selected]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/giveaways");
      const next = data.giveaways || [];
      setGiveaways(next);
      setSelectedId((current) => next.find((item) => item.id === current)?.id || next[0]?.id || "");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load giveaways.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const replaceGiveaway = (updated) => {
    setGiveaways((current) => {
      const exists = current.some((item) => item.id === updated.id);
      if (!exists) return [updated, ...current];
      return current.map((item) => (item.id === updated.id ? updated : item));
    });
    setSelectedId(updated.id);
  };

  const runAction = async (label, action) => {
    setBusy(label);
    setError("");
    setMessage("");
    try {
      const updated = await action();
      if (updated) replaceGiveaway(updated);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || "Giveaway action failed.");
    } finally {
      setBusy("");
    }
  };

  const createGiveaway = async (event) => {
    event.preventDefault();
    await runAction("create", async () => {
      const { data } = await api.post("/admin/giveaways", giveawayPayload(giveawayForm));
      setGiveawayForm(blankGiveaway);
      setMessage("Giveaway created.");
      return data;
    });
  };

  const saveGiveaway = async () => {
    if (!selected) return;
    await runAction("save", async () => {
      const { data } = await api.patch(`/admin/giveaways/${selected.id}`, giveawayPayload(editForm));
      setEditing(false);
      setMessage("Giveaway updated.");
      return data;
    });
  };

  const deleteGiveaway = async () => {
    if (!selected) return;
    const ok = window.confirm(`Delete "${selected.title}"? This removes the giveaway and its entries.`);
    if (!ok) return;
    setBusy("delete");
    setError("");
    setMessage("");
    try {
      await api.delete(`/admin/giveaways/${selected.id}`);
      setMessage("Giveaway deleted.");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not delete giveaway.");
    } finally {
      setBusy("");
    }
  };

  const addEntry = async (event) => {
    event.preventDefault();
    if (!selected) return;
    await runAction("entry", async () => {
      const { data } = await api.post(`/admin/giveaways/${selected.id}/entries`, entryForm);
      setEntryForm(blankEntry);
      setMessage("Entry added.");
      return data;
    });
  };

  const removeEntry = async (entryId) => {
    if (!selected) return;
    await runAction(`remove-${entryId}`, async () => {
      const { data } = await api.delete(`/admin/giveaways/${selected.id}/entries/${entryId}`);
      setMessage("Entry removed.");
      return data;
    });
  };

  const pickWinner = async () => {
    if (!selected) return;
    await runAction("pick", async () => {
      const { data } = await api.post(`/admin/giveaways/${selected.id}/pick-winner`);
      setMessage("Winner selected. Show the winner/wheel on OBS, then spin for the prize.");
      return data;
    });
  };

  const completeGiveaway = async () => {
    if (!selected) return;
    await runAction("complete", async () => {
      const { data } = await api.post(`/admin/giveaways/${selected.id}/complete`);
      setMessage("Giveaway completed.");
      return data;
    });
  };

  const showOnOverlay = async () => {
    if (!selected) return;
    setBusy("overlay-show");
    setError("");
    setMessage("");
    try {
      await api.post(`/admin/giveaways/${selected.id}/overlay/show`, {
        duration: Number(overlayDuration) > 0 ? Number(overlayDuration) : undefined,
        mode: "winner",
      });
      setMessage("Winner and prize wheel shown on OBS.");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not show winner on overlay.");
    } finally {
      setBusy("");
    }
  };

  const spinPrizeWheel = async () => {
    if (!selected) return;
    setBusy("prize-spin");
    setError("");
    setMessage("");
    try {
      const { data } = await api.post(`/admin/giveaways/${selected.id}/spin-prize`, {
        duration: Number(overlayDuration) > 0 ? Number(overlayDuration) : undefined,
      });
      if (data.giveaway) replaceGiveaway(data.giveaway);
      setMessage(data.giveaway?.selected_prize ? `Prize wheel spun: ${data.giveaway.selected_prize}` : "Prize wheel spun.");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not spin the prize wheel.");
    } finally {
      setBusy("");
    }
  };

  const clearOverlay = async () => {
    if (!selected) return;
    setBusy("overlay-clear");
    setError("");
    setMessage("");
    try {
      await api.post(`/admin/giveaways/${selected.id}/overlay/clear`);
      setMessage("Overlay winner cleared.");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not clear overlay winner.");
    } finally {
      setBusy("");
    }
  };

  const isCompleted = selected?.status === "completed";

  return (
    <div className="space-y-6" data-testid="admin-giveaways-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Admin-only MVP</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <Gift className="w-5 h-5 text-[hsl(var(--primary))]" />
            Giveaway / Wheel
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
            Add manual/free entries, manage Giveaway Token entries, pick a winner, show the wheel on OBS, then spin for the prize.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading || !!busy}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white disabled:opacity-50"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {(error || message) && (
        <div className={`surface p-4 text-sm ${error ? "text-[hsl(var(--destructive))]" : "text-emerald-300"}`}>
          {error || message}
        </div>
      )}

      <section className="surface p-5">
        <div className="label-eyebrow mb-3">Create giveaway</div>
        <form onSubmit={createGiveaway} className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr_160px] gap-3 items-start">
          <Field label="Title">
            <input
              value={giveawayForm.title}
              onChange={(event) => setGiveawayForm({ ...giveawayForm, title: event.target.value })}
              maxLength={80}
              required
              className={inputClass}
              placeholder="Friday night wheel"
            />
          </Field>
          <Field label="Description">
            <input
              value={giveawayForm.description}
              onChange={(event) => setGiveawayForm({ ...giveawayForm, description: event.target.value })}
              maxLength={240}
              className={inputClass}
              placeholder="Optional streamer note"
            />
          </Field>
          <Field label="Token cost">
            <input
              type="number"
              min="0"
              value={giveawayForm.entry_cost_amount}
              onChange={(event) => setGiveawayForm({ ...giveawayForm, entry_cost_amount: event.target.value })}
              className={inputClass}
            />
          </Field>
          <div className="lg:col-span-3">
            <PrizeListEditor
              label="Prizes"
              prizes={giveawayForm.prizes}
              onChange={(prizes) => setGiveawayForm({ ...giveawayForm, prizes })}
            />
          </div>
          <button
            type="submit"
            disabled={busy === "create"}
            className="lg:col-span-3 inline-flex justify-center items-center gap-2 h-10 px-4 rounded bg-[hsl(var(--primary))] text-white text-sm disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> Create
          </button>
        </form>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-6">
        <section className="surface overflow-hidden">
          <div className="p-5 border-b border-[hsl(var(--border))]">
            <div className="label-eyebrow">Active / recent</div>
            <h2 className="text-lg font-semibold mt-1">Giveaways</h2>
          </div>
          {loading ? (
            <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">Loading giveaways...</div>
          ) : giveaways.length === 0 ? (
            <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">
              No giveaways yet. Create one above, then add entries.
            </div>
          ) : (
            <div>
              <GiveawayListSection
                title="Active giveaways"
                empty="No active giveaways."
                giveaways={activeGiveaways}
                selectedId={selected?.id}
                onSelect={setSelectedId}
              />
              <GiveawayListSection
                title="Recent completed"
                empty="No completed giveaways yet."
                giveaways={completedGiveaways}
                selectedId={selected?.id}
                onSelect={setSelectedId}
              />
            </div>
          )}
        </section>

        <section className="surface min-h-[460px]">
          {!selected ? (
            <div className="p-6 text-sm text-[hsl(var(--muted-foreground))]">
              Select or create a giveaway to manage entries and overlay output.
            </div>
          ) : (
            <div className="p-5 space-y-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="min-w-0">
                  <div className="label-eyebrow">Selected giveaway</div>
                  {editing ? (
                    <div className="mt-2 grid grid-cols-1 lg:grid-cols-[1fr_160px] gap-3">
                      <Field label="Title">
                        <input
                          value={editForm.title}
                          onChange={(event) => setEditForm({ ...editForm, title: event.target.value })}
                          maxLength={80}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Description">
                        <input
                          value={editForm.description}
                          onChange={(event) => setEditForm({ ...editForm, description: event.target.value })}
                          maxLength={240}
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Token cost">
                        <input
                          type="number"
                          min="0"
                          value={editForm.entry_cost_amount}
                          onChange={(event) => setEditForm({ ...editForm, entry_cost_amount: event.target.value })}
                          className={inputClass}
                        />
                      </Field>
                      <div className="lg:col-span-2">
                        <PrizeListEditor
                          label="Prize wheel items"
                          prizes={editForm.prizes}
                          onChange={(prizes) => setEditForm({ ...editForm, prizes })}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-2xl font-semibold tracking-tight mt-1">{selected.title}</h2>
                      {selected.description && (
                        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{selected.description}</p>
                      )}
                      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2">
                        Token cost: {selected.entry_cost_amount ?? 1} Giveaway Tokens per public/chat entry
                        {selected.prizes?.length ? ` - ${selected.prizes.length} prize items` : " - no prizes configured"}
                      </div>
                      {selected.prizes?.length > 0 && (
                        <div className="mt-4 rounded border border-[hsl(var(--border))] bg-black/30 p-3">
                          <div className="label-eyebrow mb-2">Prize wheel</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {selected.prizes.map((prize, index) => (
                              <div key={`${prize}-${index}`} className="rounded border border-[hsl(var(--border))] px-3 py-2 text-sm text-[#f3ead3]">
                                <span className="text-[hsl(var(--muted-foreground))] mr-2">#{index + 1}</span>{prize}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusPill status={selected.status} />
                  {editing ? (
                    <button
                      type="button"
                      onClick={saveGiveaway}
                      disabled={busy === "save"}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded bg-[hsl(var(--primary))] text-white text-sm disabled:opacity-40"
                    >
                      <Save className="w-4 h-4" /> Save
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[hsl(var(--border))] text-sm text-[hsl(var(--muted-foreground))] hover:text-white"
                    >
                      <Pencil className="w-4 h-4" /> Edit
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={deleteGiveaway}
                    disabled={busy === "delete"}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[hsl(var(--destructive))]/60 text-sm text-[hsl(var(--destructive))] disabled:opacity-40"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </div>

              {selected.winner && (
                <div className="rounded border border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 p-5">
                  <div className="label-eyebrow flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-[hsl(var(--primary))]" /> Selected winner
                  </div>
                  <div className="text-3xl font-semibold mt-2 text-[#f3ead3]">{selected.winner.display_name}</div>
                  <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                    @{selected.winner.username}
                    {selected.selected_prize ? ` - prize: ${selected.selected_prize}` : " - prize not spun yet"}
                  </div>
                  <div className="mt-4 flex flex-col md:flex-row md:items-end gap-3">
                    <Field label="Overlay seconds">
                      <input
                        type="number"
                        min="1"
                        max="120"
                        value={overlayDuration}
                        onChange={(event) => setOverlayDuration(event.target.value)}
                        className={inputClass}
                      />
                    </Field>
                    <button
                      type="button"
                      onClick={showOnOverlay}
                      disabled={busy === "overlay-show"}
                      className="inline-flex justify-center items-center gap-2 h-10 px-4 rounded bg-[hsl(var(--primary))] text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Eye className="w-4 h-4" /> Show Winner + Wheel
                    </button>
                    <button
                      type="button"
                      onClick={spinPrizeWheel}
                      disabled={busy === "prize-spin" || !selected.prizes?.length}
                      className="inline-flex justify-center items-center gap-2 h-10 px-4 rounded border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-sm hover:text-white hover:bg-[hsl(var(--primary))]/15 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Shuffle className="w-4 h-4" /> Spin Prize Wheel
                    </button>
                    <button
                      type="button"
                      onClick={clearOverlay}
                      disabled={busy === "overlay-clear"}
                      className="inline-flex justify-center items-center gap-2 h-10 px-4 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] text-sm hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <EyeOff className="w-4 h-4" /> Clear Overlay
                    </button>
                    <button
                      type="button"
                      onClick={completeGiveaway}
                      disabled={isCompleted || !selected.winner || busy === "complete"}
                      className="inline-flex justify-center items-center gap-2 h-10 px-4 rounded border border-emerald-500 text-emerald-300 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Trophy className="w-4 h-4" /> Mark Completed
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={addEntry} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_0.8fr_auto] gap-3 items-end">
                <Field label="Viewer username">
                  <input
                    value={entryForm.username}
                    onChange={(event) => setEntryForm({ ...entryForm, username: event.target.value })}
                    maxLength={64}
                    disabled={isCompleted}
                    required
                    className={inputClass}
                    placeholder="thebarracksrat"
                  />
                </Field>
                <Field label="Display name">
                  <input
                    value={entryForm.display_name}
                    onChange={(event) => setEntryForm({ ...entryForm, display_name: event.target.value })}
                    maxLength={80}
                    disabled={isCompleted}
                    className={inputClass}
                    placeholder="Optional"
                  />
                </Field>
                <Field label="Source">
                  <input
                    value={entryForm.source}
                    onChange={(event) => setEntryForm({ ...entryForm, source: event.target.value })}
                    maxLength={40}
                    disabled={isCompleted}
                    className={inputClass}
                    placeholder="manual"
                  />
                </Field>
                <button
                  type="submit"
                  disabled={isCompleted || busy === "entry"}
                  className="inline-flex justify-center items-center gap-2 h-10 px-4 rounded border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
              </form>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  type="button"
                  onClick={pickWinner}
                  disabled={isCompleted || selected.entry_count < 1 || busy === "pick"}
                  className="inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded bg-[hsl(var(--primary))] text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Shuffle className="w-4 h-4" /> Pick Winner
                </button>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">
                  Pick Winner chooses the viewer. Show Winner + Wheel sends it to OBS. Spin Prize Wheel lands on the prize.
                </span>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <div className="label-eyebrow">Entries</div>
                    <h3 className="text-lg font-semibold mt-1">{selected.entry_count} entered</h3>
                  </div>
                </div>
                {selected.entries.length === 0 ? (
                  <div className="rounded border border-[hsl(var(--border))] p-5 text-sm text-[hsl(var(--muted-foreground))]">
                    No entries yet. Add a viewer manually or let signed-in viewers enter from the public Giveaways page or chat.
                  </div>
                ) : (
                  <div className="rounded border border-[hsl(var(--border))] overflow-hidden">
                    <div className="grid grid-cols-[1fr_auto] md:grid-cols-[1.2fr_1fr_0.8fr_auto] gap-3 px-4 py-2 text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] bg-black/30">
                      <span>Name</span>
                      <span className="hidden md:block">Username</span>
                      <span className="hidden md:block">Source</span>
                      <span />
                    </div>
                    {selected.entries.map((entry) => (
                      <div key={entry.id} className="grid grid-cols-[1fr_auto] md:grid-cols-[1.2fr_1fr_0.8fr_auto] gap-3 px-4 py-3 border-t border-[hsl(var(--border))] items-center">
                        <div>
                          <div className="font-medium">{entry.display_name}</div>
                          <div className="md:hidden text-xs text-[hsl(var(--muted-foreground))]">@{entry.username} - {entry.source}</div>
                        </div>
                        <div className="hidden md:block text-sm text-[hsl(var(--muted-foreground))]">@{entry.username}</div>
                        <div className="hidden md:block text-sm text-[hsl(var(--muted-foreground))]">{entry.source}</div>
                        <button
                          type="button"
                          onClick={() => removeEntry(entry.id)}
                          disabled={isCompleted || busy === `remove-${entry.id}`}
                          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label={`Remove ${entry.display_name}`}
                        >
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ label = "", children } = {}) {
  return (
    <label className="block text-sm">
      <span className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">{label}</span>
      {children}
    </label>
  );
}

function PrizeListEditor({ label = "", prizes, onChange } = {}) {
  const list = Array.isArray(prizes) && prizes.length ? prizes : [""];
  const updatePrize = (index, value) => {
    const next = [...list];
    next[index] = value;
    onChange(next);
  };
  const addPrize = () => onChange([...list, ""]);
  const removePrize = (index) => {
    const next = list.filter((_, itemIndex) => itemIndex !== index);
    onChange(next.length ? next : [""]);
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{label}</span>
        <button
          type="button"
          onClick={addPrize}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white"
        >
          <Plus className="w-3 h-3" /> Add prize
        </button>
      </div>
      <div className="space-y-2">
        {list.map((prize, index) => (
          <div key={index} className="grid grid-cols-[auto_1fr_auto] gap-2 items-center">
            <div className="w-8 h-10 rounded border border-[hsl(var(--border))] bg-black/30 text-xs text-[hsl(var(--muted-foreground))] flex items-center justify-center">
              {index + 1}
            </div>
            <input
              value={prize}
              onChange={(event) => updatePrize(index, event.target.value)}
              maxLength={120}
              className={inputClass}
              placeholder="Prize name"
            />
            <button
              type="button"
              onClick={() => removePrize(index)}
              className="h-10 w-10 inline-flex items-center justify-center rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"
              aria-label={`Remove prize ${index + 1}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
        Each row becomes one section on the prize wheel.
      </p>
    </div>
  );
}

function GiveawayListSection({ title, empty, giveaways, selectedId, onSelect }) {
  return (
    <div className="border-t border-[hsl(var(--border))] first:border-t-0">
      <div className="px-4 pt-4 pb-2 text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
        {title}
      </div>
      {giveaways.length === 0 ? (
        <div className="px-4 pb-4 text-sm text-[hsl(var(--muted-foreground))]">{empty}</div>
      ) : (
        <div className="divide-y divide-[hsl(var(--border))]">
          {giveaways.map((giveaway) => (
            <button
              key={giveaway.id}
              type="button"
              onClick={() => onSelect(giveaway.id)}
              className={`w-full text-left p-4 hover:bg-[hsl(var(--surface-2))] transition ${
                selectedId === giveaway.id ? "bg-[hsl(var(--surface-2))]" : ""
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{giveaway.title}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    {giveaway.entry_count} entries - {giveaway.entry_cost_amount ?? 1} Giveaway Tokens
                    {giveaway.winner ? ` - winner: ${giveaway.winner.display_name}` : ""}
                    {giveaway.selected_prize ? ` - prize: ${giveaway.selected_prize}` : ""}
                  </div>
                </div>
                <StatusPill status={giveaway.status} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const completed = status === "completed";
  return (
    <span className={`shrink-0 text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${
      completed
        ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
        : "border-[hsl(var(--primary))]/60 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10"
    }`}>
      {completed ? "Completed" : "Active"}
    </span>
  );
}
